/**
 * Module-level external store for in-progress agent feed items.
 * WebSocket event handlers write state transitions here; React components
 * read via useSyncExternalStore (see hooks.ts).
 *
 * Item bodies are held in a `createRecordStore<AgentFeedItem>` toolkit
 * instance keyed by `item.id`. This module owns two things the toolkit
 * doesn't know about: `order` (the newest-first id list the feed renders
 * in) and `wsActivity` (REST/WS reconciliation bookkeeping). Every public
 * mutation below updates `order` and the toolkit body together, then
 * rebuilds the cached `snapshot` array.
 */

import { createRecordStore } from '@/lib/store/createRecordStore';

import type { AgentFeedItem } from '../types';

// ---------------------------------------------------------------------------
// Module-level state (private)
// ---------------------------------------------------------------------------

const store = createRecordStore<AgentFeedItem>();

/** Newest-first list of item ids. The toolkit holds bodies; this holds order. */
let order: string[] = [];

/** Cached, stable-until-next-mutation array view for getSnapshot(). */
let snapshot: ReadonlyArray<AgentFeedItem> = [];

// ---------------------------------------------------------------------------
// WS-activity tracking (for race-safe REST snapshot reconciliation)
// ---------------------------------------------------------------------------
//
// A REST snapshot is a point-in-time view computed on the server. While that
// request is in flight, WebSocket events can mutate the store. Blindly
// replacing the store with the snapshot (the old replaceAll behaviour) would
// clobber those concurrent events — transiently dropping a just-arrived
// in-progress item, or resurrecting one that was just completed.
//
// To reconcile safely, every WS-driven mutation records the wall-clock time it
// happened, keyed by item id, plus whether it left the item present
// (added/updated) or removed (completed). applyServerSnapshot() compares those
// timestamps against the time the REST fetch began: any WS mutation newer than
// the fetch start wins over the (older) snapshot. This mirrors the seq-guarded
// REST/WS merge in crmObjectStore, using timestamps since this store has no seq.

interface WsActivity {
  at: number;
  present: boolean;
}

let wsActivity = new Map<string, WsActivity>();

/** Record that a WS event left `id` present (added/updated) or removed it. */
function markWsActivity(id: string, present: boolean): void {
  wsActivity.set(id, { at: Date.now(), present });
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/** Rebuild the cached snapshot array from `order` + the toolkit's bodies. */
function rebuildSnapshot(): void {
  const next: AgentFeedItem[] = [];
  for (const id of order) {
    const data = store.getData(id);
    if (data) {
      next.push(data);
    }
  }
  snapshot = next;
}

/**
 * Fully overwrite a record's body, ignoring whatever the toolkit's seq guard
 * would otherwise do. This store has no real seq (every write passes the
 * constant 0), so a plain `upsert` on an existing id would be seen as a
 * no-op replay and skipped — remove first so `upsert` always takes the
 * "no existing record" path and applies unconditionally.
 */
function writeBody(item: AgentFeedItem): void {
  store.beginBatch();
  store.remove(item.id);
  store.upsert(item.id, item, 0, {
    isComplete: true,
    merge: (_existing, incoming) => incoming,
  });
  store.endBatch();
}

// ---------------------------------------------------------------------------
// Public read/subscribe API
// ---------------------------------------------------------------------------

/** Returns the current snapshot. Stable reference until next mutation. */
export function getSnapshot(): ReadonlyArray<AgentFeedItem> {
  return snapshot;
}

/** Subscribe to store changes. Returns an unsubscribe function. */
export function subscribe(listener: () => void): () => void {
  return store.subscribe(listener);
}

// ---------------------------------------------------------------------------
// Public mutation API
// ---------------------------------------------------------------------------

/**
 * Add a new item (prepended) or replace an existing item with the same id
 * (preserving its position).
 *
 * `rebuildSnapshot()` runs before the outer `endBatch()` (not after) so that
 * `snapshot` already reflects the new data by the time `endBatch()` notifies
 * subscribers — `useSyncExternalStore` re-checks `getSnapshot()` synchronously
 * inside that notification, so a stale `snapshot` at that point silently
 * drops the re-render.
 */
export function addOrUpdateItem(item: AgentFeedItem): void {
  store.beginBatch();
  if (!order.includes(item.id)) {
    order = [item.id, ...order];
  }
  writeBody(item);
  markWsActivity(item.id, true);
  rebuildSnapshot();
  store.endBatch();
}

/**
 * Transition a trigger item to a conversation item, or create a new
 * conversation item if the trigger was created before the WS connected.
 */
export function transitionTriggerToConversation(
  triggerId: number,
  conversationId: number
): void {
  const triggerIdStr = `trigger_${triggerId}`;
  const convIdStr = `conv_${conversationId}`;
  const idx = order.indexOf(triggerIdStr);

  if (idx !== -1) {
    const existing = store.getData(triggerIdStr);
    const convIdx = order.indexOf(convIdStr);

    store.beginBatch();
    if (convIdx !== -1) {
      // A conv_* row for this conversation already exists (e.g., out-of-order
      // duplicate agent.started). Drop the trigger_* row to avoid two
      // in-progress rows for the same conversation.
      order = order.filter(id => id !== triggerIdStr);
      store.remove(triggerIdStr);
    } else if (existing) {
      const updated: AgentFeedItem = {
        ...existing,
        id: convIdStr,
        type: 'conversation',
        status: 'preparing',
        conversationId,
      };
      const nextOrder = [...order];
      nextOrder[idx] = convIdStr;
      order = nextOrder;
      store.remove(triggerIdStr);
      store.remove(convIdStr);
      store.upsert(convIdStr, updated, 0, {
        isComplete: true,
        merge: (_existing, incoming) => incoming,
      });
    }
    // The trigger row (if any) is gone and the conversation row is now present.
    markWsActivity(triggerIdStr, false);
    markWsActivity(convIdStr, true);
    rebuildSnapshot();
    store.endBatch();
    return;
  }

  // Fallback: trigger was created before WS connected, OR this is a repeated
  // agent.started for a conv we already added via fallback. Dedupe by id.
  const existingConvIdx = order.indexOf(convIdStr);
  if (existingConvIdx !== -1) {
    // Mark WS activity even on the dedupe path so that a concurrent REST
    // snapshot reconciliation sees this item as "recently confirmed by WS"
    // and does not drop it. The body is unchanged, so no store write (and no
    // subscriber notification) is needed here.
    markWsActivity(convIdStr, true);
    rebuildSnapshot();
    return;
  }

  const fallback: AgentFeedItem = {
    id: convIdStr,
    type: 'conversation',
    status: 'preparing',
    entityName: '',
    triggerSources: [],
    triggerId,
    conversationId,
    createdAt: new Date().toISOString(),
  };
  store.beginBatch();
  order = [convIdStr, ...order];
  writeBody(fallback);
  markWsActivity(triggerIdStr, false);
  markWsActivity(convIdStr, true);
  rebuildSnapshot();
  store.endBatch();
}

/**
 * Remove an in-progress item when its conversation completes. The outcome and
 * proposalId are accepted for forward compatibility but not stored here — the
 * done-today list is owned by REST.
 */
export function completeItem(
  conversationId: number,
  _outcome: string,
  _proposalId?: number
): void {
  const id = `conv_${conversationId}`;
  // Tombstone unconditionally — even when the item isn't tracked locally, an
  // in-flight REST snapshot may still contain it (the server hadn't processed
  // the completion when it built the snapshot), and reconciliation must not
  // resurrect it.
  markWsActivity(id, false);
  if (!order.includes(id)) {
    return; // item wasn't present, no-op
  }
  store.beginBatch();
  store.remove(id);
  order = order.filter(i => i !== id);
  rebuildSnapshot();
  store.endBatch();
}

/** Remove an item by id. No-op if not present. */
export function removeItem(id: string): void {
  markWsActivity(id, false);
  if (!order.includes(id)) {
    return;
  }
  store.beginBatch();
  store.remove(id);
  order = order.filter(i => i !== id);
  rebuildSnapshot();
  store.endBatch();
}

/**
 * Hard-replace the entire item list and forget all WS-activity tracking.
 *
 * This is a true reset — use it for org switches / disabling the feed, where
 * stale state from the previous org must not leak. For applying a REST snapshot
 * concurrently with live WS events, use {@link applyServerSnapshot} instead,
 * which preserves events that arrived during the fetch.
 */
export function replaceAll(newItems: AgentFeedItem[]): void {
  store.beginBatch();
  for (const id of order) {
    store.remove(id);
  }
  order = newItems.map(item => item.id);
  for (const item of newItems) {
    writeBody(item);
  }
  wsActivity = new Map();
  rebuildSnapshot();
  store.endBatch();
  store.notify();
}

/**
 * Reconcile a REST snapshot into the store without clobbering WebSocket events
 * that arrived while the fetch was in flight.
 *
 * `fetchStartedAt` is the wall-clock time the REST request was initiated. The
 * snapshot reflects server state at (or after) that moment, but cannot reflect
 * WS events the client applied after the request left. So:
 *   - snapshot items win by default (authoritative point-in-time view), EXCEPT
 *     ones a WS completion/removal tombstoned after the fetch began — those stay
 *     gone (don't resurrect a just-completed item).
 *   - for an item present in both, prefer the store's copy when a WS event
 *     updated it after the fetch began (the store has the newer state).
 *   - store items the snapshot omits are dropped as stale UNLESS a WS event
 *     added/updated them after the fetch began — those are preserved (don't
 *     transiently drop a just-arrived in-progress item).
 *
 * Preserved (newer) WS items are kept ahead of the snapshot, matching the
 * newest-first ordering of {@link addOrUpdateItem}. WS-activity records older
 * than this fetch are pruned afterwards — they've been folded into the snapshot.
 */
export function applyServerSnapshot(
  snapshotItems: AgentFeedItem[],
  fetchStartedAt: number
): void {
  const snapshotIds = new Set(snapshotItems.map(i => i.id));

  const wonAfterFetch = (id: string, present: boolean): boolean => {
    const activity = wsActivity.get(id);
    return (
      activity !== undefined &&
      activity.present === present &&
      activity.at >= fetchStartedAt
    );
  };

  // Items a WS event introduced after the snapshot was captured (newest first).
  const preservedIds: string[] = [];
  for (const id of order) {
    if (!snapshotIds.has(id) && wonAfterFetch(id, true)) {
      preservedIds.push(id);
    }
  }

  // Snapshot survivors: drop anything a newer WS event removed, and prefer the
  // store's copy for anything a newer WS event updated.
  const survivors: AgentFeedItem[] = [];
  for (const snapItem of snapshotItems) {
    if (wonAfterFetch(snapItem.id, false)) {
      continue; // removed by WS after the snapshot was captured
    }
    if (wonAfterFetch(snapItem.id, true)) {
      survivors.push(store.getData(snapItem.id) ?? snapItem);
    } else {
      survivors.push(snapItem);
    }
  }

  const nextOrder = [...preservedIds, ...survivors.map(i => i.id)];
  const nextIds = new Set(nextOrder);

  store.beginBatch();
  for (const id of order) {
    if (!nextIds.has(id)) {
      store.remove(id);
    }
  }
  for (const survivor of survivors) {
    writeBody(survivor);
  }
  order = nextOrder;
  rebuildSnapshot();
  store.endBatch();
  store.notify();

  // Prune activity that predates this fetch — it's now folded into the snapshot.
  for (const [id, activity] of wsActivity) {
    if (activity.at < fetchStartedAt) {
      wsActivity.delete(id);
    }
  }
}

/** Reset all state. Call from beforeEach in store tests only. */
export function __resetForTesting(): void {
  store.reset();
  order = [];
  wsActivity = new Map();
  rebuildSnapshot();
}
